import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { projects } from '../data/mock';
import { Button } from '../components/ui/button';

const Portfolio = () => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState('Tous');

  const categories = ['Tous', 'Ravalement de façade', 'Isolation par extérieur', 'Pose à neuf de plaquettes / Rénovation de brique'];
  
  const filteredProjects = selectedCategory === 'Tous' 
    ? projects 
    : projects.filter(p => p.category === selectedCategory);

  const openGallery = (project, imageIndex = 0) => {
    setSelectedProject(project);
    setCurrentImageIndex(imageIndex);
  };

  const closeGallery = () => {
    setSelectedProject(null);
    setCurrentImageIndex(0);
  };

  const nextImage = () => {
    if (selectedProject) {
      setCurrentImageIndex((prev) =>
        prev === selectedProject.images.length - 1 ? 0 : prev + 1
      );
    }
  };

  const prevImage = () => {
    if (selectedProject) {
      setCurrentImageIndex((prev) =>
        prev === 0 ? selectedProject.images.length - 1 : prev - 1
      );
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-gray-50 to-cyan-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Nos réalisations
            </h1>
            <p className="text-xl text-gray-600">
              Découvrez nos projets récents et laissez-vous inspirer par notre savoir-faire
            </p>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-4">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-3 rounded-full font-semibold transition-all ${
                  selectedCategory === category
                    ? 'bg-cyan-600 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Gallery */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900">
              {selectedCategory === 'Tous' ? 'Quelques projets' : selectedCategory}
              <span className="text-cyan-600 ml-3">({filteredProjects.length})</span>
            </h2>
          </div>
          <div className="grid gap-12">
            {filteredProjects.map((project) => (
              <Card key={project.id} className="border-gray-200 hover:border-cyan-500 overflow-hidden hover:shadow-xl transition-all duration-300">
                <CardContent className="p-0">
                  <div className="grid md:grid-cols-3 gap-4 p-6">
                    <div className="md:col-span-1 space-y-4">
                      <div className="inline-block bg-cyan-100 text-cyan-700 px-3 py-1 rounded-full text-xs font-semibold mb-2">
                        {project.category}
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900">{project.title}</h3>
                      <div className="space-y-2">
                        <div className="text-sm text-gray-500">Localisation</div>
                        <div className="font-medium text-gray-700">{project.location}</div>
                      </div>
                      <div className="space-y-2">
                        <div className="text-sm text-gray-500">Type de projet</div>
                        <div className="font-medium text-gray-700">{project.type}</div>
                      </div>
                      <div className="space-y-2">
                        <div className="text-sm text-gray-500">Photos</div>
                        <div className="font-medium text-gray-700">{project.images.length} images</div>
                      </div>
                    </div>
                    <div className="md:col-span-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {project.images.map((image, index) => (
                        <div
                          key={index}
                          className="relative aspect-square rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-opacity group"
                          onClick={() => openGallery(project, index)}
                        >
                          <img
                            src={image}
                            alt={`${project.title} - Photo ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all flex items-center justify-center">
                            <span className="text-white opacity-0 group-hover:opacity-100 font-medium">
                              Voir
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Image Gallery Modal */}
      {selectedProject && (
        <Dialog open={!!selectedProject} onOpenChange={closeGallery}>
          <DialogContent className="max-w-6xl p-0 bg-black">
            <div className="relative">
              <button
                onClick={closeGallery}
                className="absolute top-4 right-4 z-50 p-2 bg-white rounded-full hover:bg-stone-100 transition-colors"
              >
                <X className="w-6 h-6 text-stone-900" />
              </button>
              <div className="relative h-[80vh] flex items-center justify-center">
                <img
                  src={selectedProject.images[currentImageIndex]}
                  alt={`${selectedProject.title} - ${currentImageIndex + 1}`}
                  className="max-h-full max-w-full object-contain"
                />
                {selectedProject.images.length > 1 && (
                  <>
                    <button
                      onClick={prevImage}
                      className="absolute left-4 p-3 bg-white rounded-full hover:bg-stone-100 transition-colors"
                    >
                      <ChevronLeft className="w-6 h-6 text-stone-900" />
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-4 p-3 bg-white rounded-full hover:bg-stone-100 transition-colors"
                    >
                      <ChevronRight className="w-6 h-6 text-stone-900" />
                    </button>
                  </>
                )}
              </div>
              <div className="absolute bottom-4 left-0 right-0 text-center">
                <span className="inline-block px-4 py-2 bg-white rounded-full text-sm font-medium text-stone-900">
                  {currentImageIndex + 1} / {selectedProject.images.length}
                </span>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default Portfolio;